import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { emptyValue, TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import { OidcStateParams } from '@assetaccountant/integrations/models/oidc-state-params';
import { QuickBooksService } from '@assetaccountant/integrations/services/quickbooks.service';

@Component({
  selector: 'app-quickbooks-select-register',
  templateUrl: './quickbooks-select-register.component.html',
  styleUrls: ['./quickbooks-select-register.component.scss'],
  standalone: false,
})
export class QuickBooksSelectRegisterComponent implements OnInit {
  form = this.fb.group({
    organisationId: [emptyValue<number>()],
    registerId: [emptyValue<number>()],
  });

  isRealmConnection = false;
  isConnecting = false;
  returnRoute: string;
  realmId: string;
  code: string;

  get currentYear() {
    return new Date().getFullYear();
  }

  get title() {
    return { pageTitle: 'Set up QuickBooks Connection' };
  }

  constructor(
    private fb: TypedFormBuilder,
    private router: Router,
    @Inject(ActivatedRoute)
    private route: TypedActivatedRoute<
      {},
      {},
      { realmId: string; code: string; state: string; error: string }
    >,
    private quickBooksService: QuickBooksService,
    private trackingService: TrackingService,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const localState = localStorage.getItem('aa-oidc-state');

      if (params && localState === params.state) {
        const stateParams: OidcStateParams = JSON.parse(atob(params.state));
        if (params.error) {
          this.router.navigate([stateParams.returnRoute]);
          throw params.error;
        }

        this.returnRoute = stateParams.returnRoute;
        this.realmId = params.realmId;
        this.code = params.code;

        const redirectMatch = /\bregisters\/(\d+)/.exec(stateParams.returnRoute);
        const registerId = (redirectMatch && redirectMatch[1]) || '';

        if (registerId) {
          this.quickBooksService
            .connect({
              authorizationCode: params.code,
              registerId: +registerId,
            })
            .subscribe({
              next: () => {
                location.href = stateParams.returnRoute;
              },
              error: error => {
                location.href = stateParams.returnRoute;
                throw error;
              },
            });
        } else {
          this.isRealmConnection = true;
        }
      } else {
        this.router.navigate(['/organisations/-']);
      }
    });
  }

  onConnectClick() {
    this.isConnecting = true;
    this.form.disable();

    const command = {
      registerId: +this.form.value.registerId || undefined,
      organisationId: +this.form.value.organisationId || undefined,
      code: this.code,
    };

    if (!this.form.value.organisationId) {
      this.trackingService.initaliseUserTracking(true);
      this.trackingService.startSessionReplay();
      this.trackingService.trackEvent('Create new organisation (app store)', 'QuickBooks');
    }

    this.quickBooksService
      .connect({
        authorizationCode: this.code,
        registerId: command.registerId,
        organisationId: command.organisationId,
      })
      .subscribe(
        response => {
          this.isConnecting = false;
          if (this.isRealmConnection) {
            this.router.navigateByUrl(
              `/organisations/${response.integration.organisationId}/registers/${response.integration.registerId}/start`,
            );
          } else {
            location.href = this.returnRoute.toString();
          }
        },
        error => {
          this.isConnecting = false;
          this.form.enable();
          this.quickBooksService.handleError(command.registerId, error, this.router.url);
        },
      );
  }

  onCancelClick() {
    location.href = this.returnRoute.toString();
    return;
  }
}
